/**
 * Privy Wallet Utilities
 *
 * Shared logic for handling Privy wallet prioritization across webapp and platform-api.
 * This ensures consistent behavior when determining which wallet to use for a user
 * who has multiple wallets linked (e.g., external Phantom + embedded EVM).
 *
 * Priority:
 * 1. External wallets (non-embedded) are preferred over embedded wallets
 * 2. Among same type, earlier firstVerifiedAt wins
 */

/**
 * Duck-typed interface for a Privy wallet account.
 * Works with both @privy-io/react-auth and @privy-io/server-auth types.
 */
export interface PrivyWalletLike {
  /** Wallet client type - "privy" indicates an embedded wallet */
  walletClientType?: string;
  /** Connector type - "embedded" indicates an embedded wallet */
  connectorType?: string;
  /** When the wallet was first verified */
  firstVerifiedAt?: Date | string | null;
  /** Chain type - "ethereum" or "solana" */
  chainType?: string;
  /** Wallet address */
  address?: string;
  /** Account type - should be "wallet" for wallet accounts */
  type?: string;
}

/**
 * Check if a wallet is embedded (Privy-created).
 * Uses both walletClientType and connectorType for robustness.
 *
 * @param wallet - A Privy wallet account (duck-typed)
 * @returns true if the wallet is embedded (Privy-created)
 */
export function isPrivyEmbeddedWallet(wallet: PrivyWalletLike): boolean {
  return (
    wallet.walletClientType === "privy" || wallet.connectorType === "embedded"
  );
}

/**
 * Sort wallets by priority:
 * 1. Non-embedded wallets first (external wallets take priority)
 * 2. Then by firstVerifiedAt (earliest first)
 *
 * This ensures that if a user logs in with an external wallet (e.g., Phantom)
 * and Privy auto-creates an embedded wallet, the external wallet is preferred.
 *
 * @param a - First wallet to compare
 * @param b - Second wallet to compare
 * @returns Comparison result for Array.sort()
 */
export function sortPrivyWalletsByPriority<T extends PrivyWalletLike>(
  a: T,
  b: T,
): number {
  const aIsEmbedded = isPrivyEmbeddedWallet(a);
  const bIsEmbedded = isPrivyEmbeddedWallet(b);

  // Non-embedded wallets come first
  if (aIsEmbedded !== bIsEmbedded) {
    return aIsEmbedded ? 1 : -1;
  }

  // Then sort by verification date (earliest first)
  const aDate = a.firstVerifiedAt;
  const bDate = b.firstVerifiedAt;

  if (aDate == null && bDate == null) return 0;
  if (aDate == null) return 1;
  if (bDate == null) return -1;

  return new Date(aDate).getTime() - new Date(bDate).getTime();
}

/**
 * Type guard to check if a linked account is a wallet with ethereum or solana chain type.
 *
 * @param account - A Privy linked account (duck-typed)
 * @returns true if the account is a wallet with a valid chain type
 */
export function isPrivyEthOrSolanaWallet(
  account: PrivyWalletLike,
): account is PrivyWalletLike & { chainType: "ethereum" | "solana" } {
  return (
    account.type === "wallet" &&
    (account.chainType === "ethereum" || account.chainType === "solana")
  );
}
